import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { breadcrumbsActions } from "@store/breadcrumbs";
import ApplicationPageTemplate from "../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate";
import TableToolbar from '../../../components/TableToolbar';
import { deleteAlert, getAlerts, updateAlert } from "@store/smart-alerts.js";
import { CHECK_FREQ } from "../utils/constants";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { Avatar, Button, Switch } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/system";
import H6 from "@components/H6";
import Subtitle1 from "@components/Subtitle1";
import ConfirmationDialog from "@components/ConfirmationDialog";
import { showSuccessMessage, showErrorMessage } from "@store/dashboard";

function ActionToolbar({ onCreate }) {
	return (
	  <TableToolbar>
		<Button
		  variant="contained"
		  onClick={onCreate}
		  startIcon={<AddIcon />}>
		  New
		</Button>
	  </TableToolbar>
	);
  }

const AlertList = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const alerts = useSelector((state) => state.smartAlerts.alerts);
	const [isFetchingData, setIsFetchingData] = useState(true);
	const [showDeleteModal, setShowDeleteModal] = useState({ show: false });
	const [toDeleteAlert, setToDeleteAlert] = useState("");

	useEffect(() => {
		dispatch(breadcrumbsActions.showMenu(true));
		dispatch(
			breadcrumbsActions.setBreadcrumbs([
				{ label: "Home", url: "/" },
				{ label: "Smart Alerts", url: "/smart-alerts" },
				{ label: "All", url: "/smart-alerts/all" },
			])
		);
	}, [dispatch]);

	const editHandler = (id) => {
		navigate("/smart-alerts/edit/" + id);
	};

	const deleteHandler = (id) => {
		setToDeleteAlert(id);
		setShowDeleteModal({ show: true });
	};

	const yesDeleteHandler = (id) => {
		dispatch(deleteAlert({ alertId: id })).then((data) => {
			if (data.payload && data.payload.status === "OK") {
				dispatch(showSuccessMessage("Alert Deleted Successfully"));
			} else {
				dispatch(showErrorMessage("Failed to delete alert"));
			}
		});
	};

	const redirectToAdd = () => {
		navigate("/smart-alerts/add");
	};

	useEffect(() => {
		setIsFetchingData(true);
		dispatch(getAlerts()).then(() => {
			setIsFetchingData(false);
		});
	}, [dispatch]);

	const statusChangeHandler = useMemo(() => {
		return (event, alert) => {
			const alertData = {
				...alert,
				status: event.target.checked ? "ENABLED" : "DISABLED",
			};
			dispatch(
				updateAlert({
					formValues: { ...alertData },
				})
			).then((data) => {
				if (data.payload && data.payload.data && data.payload.status === "OK") {
					dispatch(showSuccessMessage("Alert Status Updated Successfully"));
				} else dispatch(showErrorMessage("Failed to update alert status"));
			});
		};
	}, [dispatch]);

	return (
		<ApplicationPageTemplate
			title="Alerts List"
			description="List of the configured alerts in the system, you can use the page to add, edit or delete existing alerts"
			pageReady={!isFetchingData}
		>
			<DataGrid
				autoHeight={true}
				style={{ background: "white" }}
				rows={alerts}
				density="comfortable"
				rowHeight={100}
				disableColumnFilter={true}
				disableColumnMenu={true}
				disableColumnSelector={true}
				disableDensitySelector={true}
				disableSelectionOnClick={true}
				components={{
					Toolbar: () =>
					  ActionToolbar({
						onCreate: redirectToAdd,
					  }),
				  }}
				columns={[
					{
						field: "id",
						headerName: "ID",
						width: 80,
						sortable: false,
						headerAlign: "center",
						align: "center",
						renderCell: ({ row }) => <Avatar>{row.id}</Avatar>,
					},
					{
						field: "title",
						headerName: "Title",
						flex: 1,
						sortable: false,
						renderCell: ({ row }) => (
							<>
								<Box sx={{ width: "100%", maxWidth: 500 }}>
									<H6>{row.title}</H6>
									<Subtitle1>{row.description}</Subtitle1>
								</Box>
							</>
						),
					},
					{
						field: "type",
						headerName: "Type",
						width: 250,
						sortable: false,
						headerAlign: "center",
						align: "center",
					},
					{
						field: "checkFrequency",
						headerName: "Check Frequency",
						valueGetter: ({ row }) => CHECK_FREQ.find((i) => i.value === row.checkFrequency).text,
						width: 150,
						sortable: false,
						headerAlign: "center",
						align: "center",
					},
					{
						field: "activeTime",
						headerName: "Active Time",
						valueGetter: ({ row }) => row.activeStartTime + " - " + row.activeEndTime,
						width: 140,
						sortable: false,
						headerAlign: "center",
						align: "center",
					},
					{
						field: "modifiedBy",
						headerName: "Modified By",
						width: 140,
						sortable: false,
						headerAlign: "center",
						align: "center",
					},
					{
						field: "modifiedAt",
						headerName: "Modified At",
						valueGetter: ({ row }) => {
							return row.modifiedAt !== "" && moment(row.modifiedAt).format("DD/MM/YYYY - HH:mm");
						},
						width: 140,
						sortable: false,
						headerAlign: "center",
						align: "center",
					},
					{
						field: "status",
						name: "Status",
						width: 80,
						sortable: false,
						headerAlign: "center",
						align: "center",
						renderCell: ({ row }) => <Switch checked={row.status === "ENABLED" ? true : false} onChange={(event) => statusChangeHandler(event, row)} />,
					},
					{
						field: "actions",
						type: "actions",
						getActions: ({ row }) => [<GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={() => deleteHandler(row.id)} />, <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={() => editHandler(row.id)} />],
					},
				]}
				pageSize={5}
				rowsPerPageOptions={[5]}
			></DataGrid>
			<ConfirmationDialog openDialog={showDeleteModal} yesHandler={yesDeleteHandler} data={toDeleteAlert} title="Delete Alert" message="Are you sure you want to delete the selected Alert ?" />
		</ApplicationPageTemplate>
	);
};

export default AlertList;
